<!--
  AppField — label + control + help, the wrapper every form control uses.

  `content-start` on the root is load-bearing, not cosmetic.

  The root is a grid whose rows are the label, the control and the help text,
  all auto-sized. A grid's default `align-content` resolves to `stretch`, so
  whenever a field is given more height than its content needs — which happens
  to EVERY field in a multi-column grid, because the row takes the height of the
  tallest cell — the surplus is divided among those three rows. The label drifts
  down a few pixels, the control a few more, and two fields side by side stop
  lining up.

  Measured before the fix: two adjacent selects rendered rows of 30/40/20px and
  38/48/28px — an 8px stretch per row, leaving one label 4px and its control 8px
  below its neighbour. Subtle enough to read as a spacing bug, and to keep
  coming back.

  Pinning the content to the start keeps the rows at their natural size. Shared
  form rows additionally use subgrid to align controls under unequal labels;
  the body keeps help/errors below the control without affecting that alignment.

  Keep this comment OUTSIDE <template>: a comment before the root element makes
  the component multi-root, and `wrapper.classes()` then reads the comment node
  instead of the root div.
-->
<template>
  <div class="form-field grid grid-cols-1 content-start last:mb-0" :class="resolvedContainerClass">
    <div v-if="hasHeader" class="form-field__header mb-1.5 flex items-center gap-2" :class="headerClass">
      <!-- Keep the absolute screen-reader marker inside this field’s scroll region. -->
      <label
        v-if="label"
        :for="labelFor"
        class="form-field__label relative block font-medium text-theme-text-secondary"
        :class="labelClass"
      >
        <slot name="label">
          {{ label }}<span v-if="required" class="ml-1 text-theme-danger" aria-hidden="true">*</span>
          <span v-if="required" class="sr-only">{{ ` ${$t('forms.required')}` }}</span>
          <span v-else-if="optional" class="font-normal"> ({{ $t('forms.optional') }})</span>
        </slot>
      </label>
      <form-hint v-if="collapsedHint && !hintInControlLabel" :id="`${fieldId}-hint`" :text="hint" :label="label" />
      <form-hint v-if="collapsedHelp && !hintInControlLabel" :id="`${fieldId}-help`" :text="help" :label="label" />
    </div>

    <div class="form-field__body contents">
      <div :class="resolvedContentClass">
        <slot :described-by="fieldDescribedBy" :invalid="invalid" :hint-tooltip="hintInControlLabel && collapsedHint ? hint : ''" :help-tooltip="hintInControlLabel && collapsedHelp ? help : ''" />
      </div>

      <form-hint v-if="hint && !collapsedHint" :id="`${fieldId}-hint`" :text="hint" :label="label" />

      <slot name="help">
        <field-help
          v-if="(help && !collapsedHelp) || error || success"
          :id="`${fieldId}-help`"
          class="mt-1"
          :class="{ 'lg:col-start-2 lg:col-span-4 lg:-mt-5': horizontal }"
          :help="help || (invalid ? $t('forms.invalid') : '')"
          :error="error"
          :success="success"
        />
      </slot>
      <details v-if="tooltip" class="form-field__details">
        <summary>{{ $t('forms.how_it_works') }}</summary>
        <p>{{ tooltip }}</p>
      </details>
    </div>
  </div>
</template>

<script>
  import { computed, inject, unref } from 'vue';

  import { compactFormHintsKey } from '@/domain/formHints';
  import FormHint from '@/components/ui/forms/FormHint.vue';
  import FieldHelp from '@/components/ui/forms/FieldHelp.vue';
  import { hasValidationError, useValidationState } from '@/composables/useValidationState';
  import { getFormControlId, mergeDescribedBy } from '@/composables/useFormControlStyles';

  export default {
    name: 'AppField',
    components: {
      FieldHelp,
      FormHint,
    },
    props: {
      label: { type: String, default: '' },
      labelFor: { type: String, default: '' },
      help: { type: String, default: '' },
      hint: { type: String, default: '' },
      tooltip: { type: String, default: '' },
      hintInControlLabel: Boolean,
      required: Boolean,
      optional: Boolean,
      describedBy: { type: String, default: '' },
      horizontal: Boolean,
      addons: Boolean,
      grouped: Boolean,
      multiline: Boolean,
      error: { type: [String, Boolean, Array], default: false },
      success: { type: [String, Boolean], default: false },
      labelClass: { type: [String, Array, Object], default: '' },
      containerClass: { type: [String, Array, Object], default: '' },
      contentClass: { type: [String, Array, Object], default: '' },
    },
    setup(props, { slots }) {
      const { getValidationColor } = useValidationState();
      const generatedId = getFormControlId('app-field');
      const fieldId = computed(() => props.labelFor || generatedId);
      const compact = inject(compactFormHintsKey, false);
      const collapsedHint = computed(() => unref(compact) && Boolean(props.hint.trim()));
      const collapsedHelp = computed(() => unref(compact) && !props.error && !props.success && Boolean(props.help.trim()));
      const invalid = computed(() => hasValidationError(props.error));
      const fieldDescribedBy = computed(() => mergeDescribedBy(
        props.describedBy,
        props.hint && `${fieldId.value}-hint`,
        (props.help || invalid.value) && `${fieldId.value}-help`
      ));

      const hasHeader = computed(() => Boolean(props.label || (!props.hintInControlLabel && (collapsedHint.value || collapsedHelp.value))));

      const computedContainerClass = computed(() => {
        const base = [props.multiline ? '-mb-3' : 'mb-6'];

        if (props.horizontal) {
          base.push('form-field--horizontal lg:gap-6 lg:grid-cols-5');
        }

        if (props.containerClass) {
          base.push(props.containerClass);
        }

        return base;
      });

      const computedContentClass = computed(() => {
        const base = [];

        if (props.horizontal) {
          base.push('lg:col-span-4');

          if (!props.label) {
            base.push('lg:col-start-2');
          }
        }

        // Auto-detect multiple slot children and apply a responsive grid layout.
        // Mirrors the slot-count behaviour that was previously only in Field.vue.
        if (!props.addons && !props.grouped && !props.multiline && !props.horizontal && !props.contentClass) {
          const defaultSlots = slots.default ? slots.default({ describedBy: fieldDescribedBy.value, invalid: invalid.value }) : [];
          const slotsLength = defaultSlots.length;

          if (slotsLength > 1) {
            base.push('grid grid-cols-1 gap-3');
            if (slotsLength === 2) base.push('md:grid-cols-2');
            if (slotsLength >= 3) base.push('md:grid-cols-3');
          }
        }

        if (props.addons || props.grouped || props.multiline) {
          base.push('flex justify-start');
        }

        if (props.grouped) {
          base.push('space-x-3');
        }

        if (props.multiline) {
          base.push('flex-wrap');
        }

        if (props.contentClass) {
          base.push(props.contentClass);
        }

        return base;
      });

      const headerClass = computed(() => {
        const base = [];
        const textColor = getValidationColor(props.error, props.success);

        if (props.horizontal) {
          base.push('lg:mb-0');
        }

        if (textColor) {
          base.push(textColor);
        }

        return base;
      });

      return {
        fieldId,
        collapsedHint,
        collapsedHelp,
        invalid,
        fieldDescribedBy,
        hasHeader,
        resolvedContainerClass: computedContainerClass,
        resolvedContentClass: computedContentClass,
        headerClass,
      };
    },
  };
</script>
